import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignaturePad from 'signature_pad';
import { Button } from '..';

export type QuoteSignaturePadProps = {
    onChange: (payload: { image: string | null; source: 'draw' | 'upload' | null }) => void;
};

function QuoteSignaturePad(props: QuoteSignaturePadProps) {
    const { onChange } = props;
    const { t } = useTranslation();
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const padRef = useRef<SignaturePad | null>(null);
    const onChangeRef = useRef(onChange);
    const [mode, setMode] = useState<'draw' | 'upload'>('draw');
    const [uploadPreview, setUploadPreview] = useState<string | null>(null);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        if (mode !== 'draw' || !canvasRef.current)
            return;

        const canvas = canvasRef.current;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = canvas.offsetWidth * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        const ctx = canvas.getContext('2d');
        ctx?.scale(ratio, ratio);

        const pad = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)',
        });
        padRef.current = pad;

        const emit = () => {
            if (pad.isEmpty())
                onChangeRef.current({ image: null, source: null });
            else
                onChangeRef.current({ image: pad.toDataURL('image/png'), source: 'draw' });
        };

        pad.addEventListener('endStroke', emit);
        return () => {
            pad.removeEventListener('endStroke', emit);
            pad.off();
            padRef.current = null;
        };
    }, [mode]);

    const switchMode = (next: 'draw' | 'upload') => {
        if (next === mode)
            return;
        setMode(next);
        setUploadPreview(null);
        padRef.current?.clear();
        onChange({ image: null, source: null });
    };

    const clear = () => {
        padRef.current?.clear();
        setUploadPreview(null);
        onChange({ image: null, source: null });
    };

    const onUpload = (file?: File | null) => {
        if (!file)
            return;
        if (!file.type.startsWith('image/'))
            return;
        if (file.size > 2 * 1024 * 1024)
            return;

        const reader = new FileReader();
        reader.onload = () => {
            const image = String(reader.result || '');
            setUploadPreview(image);
            onChange({ image, source: 'upload' });
        };
        reader.readAsDataURL(file);
    };

    const tabClass = (active: boolean) => [
        'px-4 py-2 text-sm font-semibold border-b-2 -mb-px',
        active
            ? 'border-indigo-600 text-indigo-700'
            : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300',
    ].join(' ');

    return (
        <div className="rounded-md border border-gray-300 bg-white overflow-hidden">
            <div className="flex border-b border-gray-200 bg-gray-50 px-2">
                <button type="button" className={tabClass(mode === 'draw')} onClick={() => switchMode('draw')}>
                    {t('quotes.signature_draw')}
                </button>
                <button type="button" className={tabClass(mode === 'upload')} onClick={() => switchMode('upload')}>
                    {t('quotes.signature_upload')}
                </button>
            </div>

            <div className="p-4 space-y-3">
                {mode === 'draw' ? (
                    <>
                        <p className="text-sm text-gray-600">
                            {t('quotes.signature_draw_hint')}
                        </p>
                        <canvas
                            ref={canvasRef}
                            className="w-full h-44 border border-dashed border-gray-400 rounded-md bg-white touch-none cursor-crosshair"
                        />
                        <div className="flex justify-end">
                            <Button small title={t('quotes.signature_clear')} color="secondary" onClick={clear} />
                        </div>
                    </>
                ) : (
                    <>
                        <p className="text-sm text-gray-600">
                            {t('quotes.signature_upload_hint')}
                        </p>
                        <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            onChange={(e) => onUpload(e.target.files?.[0])}
                        />
                        {uploadPreview ? (
                            <div className="space-y-2">
                                <img src={uploadPreview} alt="" className="max-h-40 border rounded-md bg-white" />
                                <div className="flex justify-end">
                                    <Button small title={t('quotes.signature_clear')} color="secondary" onClick={clear} />
                                </div>
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
}

export default QuoteSignaturePad;
